import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AuthService from '../services/authService';
import customerService from '../services/customerService';
import onboardingService from '../services/onboardingService';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Onboarding state
  const [onboardingStep, setOnboardingStep] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Charger l'utilisateur au montage du composant
  useEffect(() => {
    const initAuth = () => {
      if (AuthService.isAuthenticated()) {
        const userData = AuthService.getUser();
        setUser(userData);
        setIsAuthenticated(true);
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  // Fonction de connexion
  const login = async (credentials) => {
    setIsLoading(true);
    try {
      const response = await AuthService.login(credentials);
      // `getUser()` fusionne snake_case/camelCase (dont `passwordChanged`) ;
      // `response.user` brut n'a que la forme snake_case de l'API.
      setUser(AuthService.getUser());
      setIsAuthenticated(true);

      // Déclencher l'onboarding après connexion (avec délai pour UX)
      setTimeout(() => {
        const nextStep = onboardingService.getNextStep(response.user, 'first_login');
        if (nextStep) {
          setOnboardingStep(nextStep);
          setShowOnboarding(true);
        }
      }, 1500);

      return response;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction de déconnexion
  const logout = async () => {
    setIsLoading(true);
    try {
      await AuthService.logout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction de changement de mot de passe
  const changePassword = async (passwordData) => {
    return AuthService.changePassword(passwordData);
  };

  // Fonction de réinitialisation de mot de passe
  const resetPassword = async (email) => {
    return AuthService.resetPassword(email);
  };

  // Confirme le lien reçu par email : la session retournée connecte le
  // client directement, sans lui redemander le mot de passe qu'il vient de
  // choisir.
  const confirmResetPassword = async (payload) => {
    const response = await AuthService.confirmResetPassword(payload);
    setUser(AuthService.getUser());
    setIsAuthenticated(true);
    return response;
  };

  // Vérifier une permission
  const hasPermission = (permissionCode) => {
    return AuthService.hasPermission(permissionCode);
  };

  // Vérifier un rôle
  const hasRole = (roleCode) => {
    return AuthService.hasRole(roleCode);
  };

  // Déclencher une action d'onboarding
  const triggerOnboarding = useCallback((action) => {
    if (!isAuthenticated || !user) return;

    onboardingService.trackAction(action);
    const nextStep = onboardingService.getNextStep(user, action);

    if (nextStep) {
      setOnboardingStep(nextStep);
      setShowOnboarding(true);
    }
  }, [isAuthenticated, user]);

  // Mettre à jour le profil utilisateur
  const updateProfile = async (profileData) => {
    if (!user?.id) {
      throw new Error('Utilisateur non connecté');
    }

    try {
      const response = await customerService.updateCustomer(user.id, profileData);

      // Mettre à jour les données locales
      AuthService.updateLocalUser(profileData);
      setUser(AuthService.getUser());

      return response;
    } catch (error) {
      console.error('Erreur mise à jour profil:', error);
      throw error;
    }
  };

  // Compléter une étape d'onboarding
  const completeOnboardingStep = async (data) => {
    try {
      await updateProfile(data);
      setShowOnboarding(false);
      setOnboardingStep(null);
    } catch (error) {
      throw error;
    }
  };

  // Fermer le modal d'onboarding
  const dismissOnboarding = () => {
    setShowOnboarding(false);
    setOnboardingStep(null);
  };

  // Obtenir le pourcentage de complétion du profil
  const getProfileCompletion = () => {
    return onboardingService.getProfileCompletion(user);
  };

  // Obtenir les champs manquants
  const getMissingFields = () => {
    return onboardingService.getMissingFields(user);
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    changePassword,
    resetPassword,
    confirmResetPassword,
    hasPermission,
    hasRole,
    // Onboarding
    onboardingStep,
    showOnboarding,
    triggerOnboarding,
    updateProfile,
    completeOnboardingStep,
    dismissOnboarding,
    getProfileCompletion,
    getMissingFields,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};